/**
 * ProductElementsDrawer - Drag and drop product elements onto the canvas
 * Features:
 * - Filter by element type: Strategy, Market, Personas, Goals, Initiatives, Epics, Features
 * - Search within selected type
 * - Expand/collapse items to see full details before dragging
 * - Drag creates formatted sticky note with type badge and color
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Search, Loader2, GripVertical,
  Target, BarChart3, Users, Trophy, Rocket, Package, Zap,
  FileText, Lightbulb, Layers
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { 
  productsApi, 
  personasApi, 
  goalsApi, 
  initiativesApi, 
  epicsApi, 
  featuresApi 
} from '../../services/api';
import { toast } from 'sonner';

// Element type configurations with icons and colors
const ELEMENT_TYPES = {
  strategy: {
    id: 'strategy',
    label: 'Strategy',
    icon: Target,
    color: '#a855f7', // Purple
    bgColor: '#faf5ff',
    badgeColor: 'bg-purple-100 text-purple-700',
  },
  market: {
    id: 'market',
    label: 'Market',
    icon: BarChart3,
    color: '#14b8a6', // Teal
    bgColor: '#f0fdfa',
    badgeColor: 'bg-teal-100 text-teal-700',
  },
  personas: {
    id: 'personas',
    label: 'Personas',
    icon: Users,
    color: '#ec4899', // Pink
    bgColor: '#fdf2f8',
    badgeColor: 'bg-pink-100 text-pink-700',
  },
  goals: {
    id: 'goals',
    label: 'Goals',
    icon: Trophy,
    color: '#f59e0b', // Amber
    bgColor: '#fffbeb',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
  initiatives: {
    id: 'initiatives',
    label: 'Initiatives',
    icon: Rocket,
    color: '#3b82f6', // Blue
    bgColor: '#eff6ff',
    badgeColor: 'bg-blue-100 text-blue-700',
  },
  epics: {
    id: 'epics',
    label: 'Epics',
    icon: Package,
    color: '#6366f1', // Indigo
    bgColor: '#eef2ff',
    badgeColor: 'bg-indigo-100 text-indigo-700',
  },
  features: {
    id: 'features',
    label: 'Features',
    icon: Zap,
    color: '#22c55e', // Green
    bgColor: '#f0fdf4',
    badgeColor: 'bg-green-100 text-green-700',
  },
};

const ProductElementsDrawer = ({
  productId,
  onCreateStickyNote,
  isGuest = false,
  colors,
  isOpen,
  onOpenChange,
  otherDrawerOpen = false,
}) => {
  const [drawerWidth, setDrawerWidth] = useState(() => {
    const saved = localStorage.getItem('productElements_width');
    return saved ? parseInt(saved, 10) : 500; // Default width to show all tabs
  });
  const [isResizing, setIsResizing] = useState(false);
  
  const [activeTab, setActiveTab] = useState('strategy');
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  
  // Product data for strategy/market
  const [product, setProduct] = useState(null);
  
  // Nested data for initiatives (under goals) and features (under epics)
  const [goalInitiatives, setGoalInitiatives] = useState({});
  const [epicFeatures, setEpicFeatures] = useState({});
  
  // Sync with parent state
  const isCollapsed = !isOpen;
  const setIsCollapsed = (collapsed) => onOpenChange(!collapsed);
  
  // Save width
  useEffect(() => {
    if (isGuest) return;
    if (drawerWidth && isOpen) {
      localStorage.setItem('productElements_width', drawerWidth.toString());
    }
  }, [drawerWidth, isOpen, isGuest]);
  
  // Load product data for strategy/market tabs
  useEffect(() => {
    if (!productId || isGuest) return;
    
    const loadProduct = async () => {
      try {
        const response = await productsApi.get(productId);
        setProduct(response.data);
      } catch (err) {
        console.error('Failed to load product:', err);
      }
    };
    
    loadProduct();
  }, [productId, isGuest]);
  
  // Load items when tab changes
  useEffect(() => {
    if (!productId || isGuest) return;
    
    const loadItems = async () => {
      setLoading(true);
      setItems([]);
      setExpandedItems(new Set());
      
      try {
        let data = [];
        
        switch (activeTab) {
          case 'strategy':
            // Strategy comes from product data
            if (product) {
              data = [{
                id: 'strategy-vision',
                title: 'Product Vision',
                content: product.high_level_vision || 'No vision defined',
                type: 'strategy',
              }];
              if (product.target_audience) {
                data.push({
                  id: 'strategy-audience',
                  title: 'Target Audience',
                  content: product.target_audience,
                  type: 'strategy',
                });
              }
            }
            break;
            
          case 'market':
            // Market data from product
            if (product) {
              const marketItems = [];
              if (product.competitive_landscape) {
                marketItems.push({
                  id: 'market-competitive',
                  title: 'Competitive Landscape',
                  content: product.competitive_landscape,
                  type: 'market',
                });
              }
              if (product.market_opportunity) {
                marketItems.push({
                  id: 'market-opportunity',
                  title: 'Market Opportunity',
                  content: product.market_opportunity,
                  type: 'market',
                });
              }
              if (marketItems.length === 0) {
                marketItems.push({
                  id: 'market-none',
                  title: 'Market Analysis',
                  content: 'No market data defined yet',
                  type: 'market',
                  isEmpty: true,
                });
              }
              data = marketItems;
            }
            break;
            
          case 'personas':
            const personasRes = await personasApi.list(productId);
            data = (personasRes.data || []).map(p => ({
              id: p.persona_id,
              title: p.name,
              subtitle: p.role,
              content: formatPersonaContent(p),
              shortContent: p.role || 'No role defined',
              type: 'personas',
              raw: p,
            }));
            break;
            
          case 'goals':
            const goalsRes = await goalsApi.list(productId);
            data = (goalsRes.data || []).map(g => ({
              id: g.goal_id,
              title: g.title,
              content: formatGoalContent(g),
              shortContent: g.description || 'No description',
              type: 'goals',
              raw: g,
            }));
            break;
            
          case 'initiatives':
            // Initiatives are nested under goals, so we need to load goals first
            const goalsForInit = await goalsApi.list(productId);
            const allInitiatives = [];
            const initByGoal = {};
            
            for (const goal of goalsForInit.data || []) {
              try {
                const initRes = await initiativesApi.list(goal.goal_id);
                const initiatives = initRes.data || [];
                initByGoal[goal.goal_id] = initiatives;
                
                for (const init of initiatives) {
                  allInitiatives.push({
                    id: init.initiative_id,
                    title: init.title,
                    subtitle: `Goal: ${goal.title}`,
                    content: formatInitiativeContent(init, goal),
                    shortContent: init.description || 'No description',
                    type: 'initiatives',
                    raw: init,
                    parentGoal: goal,
                  });
                }
              } catch (err) {
                console.error(`Failed to load initiatives for goal ${goal.goal_id}:`, err);
              }
            }
            
            setGoalInitiatives(initByGoal);
            data = allInitiatives;
            break;
            
          case 'epics':
            const epicsRes = await epicsApi.listByProduct(productId);
            data = (epicsRes.data || []).map(e => ({
              id: e.epic_id,
              title: e.title,
              subtitle: e.initiative_id ? 'Linked to Initiative' : 'Standalone',
              content: formatEpicContent(e),
              shortContent: e.description || 'No description',
              type: 'epics',
              raw: e,
            }));
            break;
            
          case 'features':
            // Features are nested under epics
            const epicsForFeatures = await epicsApi.listByProduct(productId);
            const allFeatures = [];
            const featByEpic = {};
            
            for (const epic of epicsForFeatures.data || []) {
              try {
                const featRes = await featuresApi.list(epic.epic_id);
                const features = featRes.data || [];
                featByEpic[epic.epic_id] = features;
                
                for (const feat of features) {
                  allFeatures.push({
                    id: feat.feature_id,
                    title: feat.title,
                    subtitle: `Epic: ${epic.title}`,
                    content: formatFeatureContent(feat, epic),
                    shortContent: feat.description || 'No description',
                    type: 'features',
                    raw: feat,
                    parentEpic: epic,
                  });
                }
              } catch (err) {
                console.error(`Failed to load features for epic ${epic.epic_id}:`, err);
              }
            }
            
            setEpicFeatures(featByEpic);
            data = allFeatures;
            break;
        }
        
        setItems(data);
      } catch (err) {
        console.error('Failed to load items:', err);
        toast.error('Failed to load items');
      } finally {
        setLoading(false);
      }
    };
    
    loadItems();
  }, [activeTab, productId, product, isGuest]);
  
  // Format functions for different element types
  const formatPersonaContent = (persona) => {
    let content = `**${persona.name}**\n`;
    if (persona.role) content += `*${persona.role}*\n\n`;
    if (persona.goals) content += `**Goals:** ${persona.goals}\n\n`;
    if (persona.pain_points) content += `**Pain Points:** ${persona.pain_points}\n\n`;
    if (persona.context) content += `**Context:** ${persona.context}`;
    return content.trim();
  };
  
  const formatGoalContent = (goal) => {
    let content = `**${goal.title}**\n\n`;
    if (goal.description) content += `${goal.description}\n\n`;
    if (goal.success_metrics) content += `**Success Metrics:** ${goal.success_metrics}\n\n`;
    if (goal.timeframe) content += `**Timeframe:** ${goal.timeframe}`;
    return content.trim();
  };
  
  const formatInitiativeContent = (init, goal) => {
    let content = `**${init.title}**\n`;
    if (goal) content += `*Goal: ${goal.title}*\n\n`;
    if (init.description) content += `${init.description}\n\n`;
    if (init.status) content += `**Status:** ${init.status}`;
    return content.trim();
  };
  
  const formatEpicContent = (epic) => {
    let content = `**${epic.title}**\n\n`;
    if (epic.description) content += `${epic.description}\n\n`;
    if (epic.acceptance_criteria) content += `**Acceptance Criteria:** ${epic.acceptance_criteria}`;
    return content.trim();
  };
  
  const formatFeatureContent = (feat, epic) => {
    let content = `**${feat.title}**\n`;
    if (epic) content += `*Epic: ${epic.title}*\n\n`;
    if (feat.description) content += `${feat.description}\n\n`;
    if (feat.priority) content += `**Priority:** ${feat.priority}\n`;
    if (feat.status) content += `**Status:** ${feat.status}`;
    return content.trim();
  };
  
  // Filter items by search query
  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.title?.toLowerCase().includes(query) ||
      item.content?.toLowerCase().includes(query) ||
      item.subtitle?.toLowerCase().includes(query)
    );
  });
  
  // Toggle item expansion
  const toggleExpand = (itemId) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };
  
  // Handle drag start
  const handleDragStart = (e, item) => {
    const typeConfig = ELEMENT_TYPES[item.type];
    const isExpanded = expandedItems.has(item.id);
    
    // Create drag data
    const dragData = {
      type: 'product-element',
      elementType: item.type,
      title: item.title,
      content: isExpanded ? item.content : (item.shortContent || item.content),
      color: typeConfig.bgColor,
      badgeText: `${typeConfig.label}`,
      badgeColor: typeConfig.badgeColor,
    };
    
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'copy';
    
    setDraggedItem(item);
    setIsDragging(true);
  };
  
  // Handle drag end
  const handleDragEnd = () => {
    setDraggedItem(null);
    setIsDragging(false);
  };
  
  // Hide for guests
  if (isGuest) return null;
  
  // Hide handle when other drawer is open
  if (otherDrawerOpen && isCollapsed) return null;
  
  // Collapsed state - handle positioned in middle, stacked above AI panel
  if (isCollapsed) {
    return (
      <div className="fixed right-0 top-[46%] z-40">
        <button
          onClick={() => setIsCollapsed(false)}
          className={cn(
            "flex items-center gap-2 px-3 py-3 rounded-l-lg shadow-lg transition-all",
            "bg-gradient-to-r from-teal-500 to-cyan-600 text-white hover:from-teal-600 hover:to-cyan-700"
          )}
        >
          <Layers className="w-5 h-5" />
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>
    );
  }
  
  const TypeIcon = ELEMENT_TYPES[activeTab]?.icon || FileText;
  
  // Handle resize
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    
    const handleMouseMove = (ev) => {
      const newWidth = window.innerWidth - ev.clientX;
      setDrawerWidth(Math.min(Math.max(newWidth, 320), 600));
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  return (
    <div
      className={cn(
        "fixed right-0 top-0 h-full z-40 flex",
        (isDragging || isResizing) && "select-none"
      )}
      style={{ width: drawerWidth }}
    >
      {/* Resize handle */}
      <div
        className="w-2 cursor-ew-resize hover:bg-teal-400 active:bg-teal-500 transition-colors"
        onMouseDown={handleResizeMouseDown}
      />
      
      {/* Panel content */}
      <div className={cn("flex-1 flex flex-col border-l shadow-2xl overflow-hidden", colors?.bgCard || "bg-white")}>
        {/* Header */}
        <div className={cn("p-3 border-b flex items-center justify-between gap-2 flex-shrink-0", colors?.borderPrimary)}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
              <Layers className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className={cn("font-semibold text-sm truncate", colors?.textPrimary)}>Product Elements</h3>
              <p className={cn("text-xs", colors?.textMuted)}>Drag to canvas</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="flex-shrink-0 h-8 w-8" onClick={() => setIsCollapsed(true)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Filter Tabs */}
        <div className={cn("p-2 border-b overflow-x-auto", colors?.borderPrimary)}>
          <div className="flex gap-1 min-w-max">
            {Object.values(ELEMENT_TYPES).map(type => {
              const Icon = type.icon;
              const isActive = activeTab === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => setActiveTab(type.id)}
                  className={cn(
                    "flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors",
                    isActive 
                      ? "bg-gray-900 text-white" 
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  )}
                >
                  <Icon className="w-3 h-3" />
                  <span className="hidden sm:inline">{type.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Search */}
        <div className={cn("p-2 border-b", colors?.borderPrimary)}>
          <div className="relative">
            <Search className={cn("absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4", colors?.textMuted)} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${ELEMENT_TYPES[activeTab]?.label || 'items'}...`}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>
        
        {/* Items List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className={cn("w-6 h-6 animate-spin", colors?.textMuted)} />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className={cn("text-center py-8 text-sm", colors?.textMuted)}>
              {searchQuery ? 'No matching items' : `No ${ELEMENT_TYPES[activeTab]?.label || 'items'} found`}
            </div>
          ) : (
            filteredItems.map(item => {
              const typeConfig = ELEMENT_TYPES[item.type];
              const isExpanded = expandedItems.has(item.id);
              const isDraggedItem = draggedItem?.id === item.id;
              
              return (
                <div
                  key={item.id}
                  draggable={!item.isEmpty}
                  onDragStart={(e) => handleDragStart(e, item)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "rounded-lg border transition-all",
                    isDraggedItem && "opacity-50",
                    item.isEmpty 
                      ? "bg-gray-50 border-dashed cursor-not-allowed" 
                      : "cursor-grab hover:shadow-md active:cursor-grabbing",
                    colors?.borderSecondary
                  )}
                  style={{ 
                    backgroundColor: item.isEmpty ? undefined : typeConfig.bgColor,
                    borderColor: item.isEmpty ? undefined : `${typeConfig.color}40`
                  }}
                >
                  <div className="p-2">
                    {/* Header row with drag handle and expand button */}
                    <div className="flex items-start gap-2">
                      {!item.isEmpty && (
                        <GripVertical className={cn("w-4 h-4 mt-0.5 flex-shrink-0", colors?.textMuted)} />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={cn("text-xs px-1.5 py-0", typeConfig.badgeColor)}>
                            <TypeIcon className="w-3 h-3 mr-1" />
                            {typeConfig.label}
                          </Badge>
                        </div>
                        
                        <h4 className={cn("font-medium text-sm truncate", colors?.textPrimary)}>
                          {item.title}
                        </h4>
                        
                        {item.subtitle && (
                          <p className={cn("text-xs truncate", colors?.textMuted)}>
                            {item.subtitle}
                          </p>
                        )}
                      </div>
                      
                      {!item.isEmpty && item.content !== item.shortContent && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(item.id);
                          }}
                          className={cn(
                            "p-1 rounded hover:bg-black/10 transition-colors flex-shrink-0",
                            colors?.textMuted
                          )}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                    
                    {/* Preview content */}
                    <div className={cn(
                      "mt-2 text-xs overflow-hidden transition-all",
                      colors?.textSecondary,
                      isExpanded ? "max-h-48" : "max-h-12"
                    )}>
                      <p className={cn(
                        "whitespace-pre-wrap",
                        !isExpanded && "line-clamp-2"
                      )}>
                        {isExpanded ? item.content : (item.shortContent || item.content)}
                      </p>
                    </div>
                    
                    {/* Expand hint */}
                    {!isExpanded && item.content !== item.shortContent && !item.isEmpty && (
                      <p className={cn("text-xs mt-1 italic", colors?.textMuted)}>
                        Click ▼ to see full details
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {/* Footer hint */}
        <div className={cn("p-2 border-t text-center", colors?.borderPrimary)}>
          <p className={cn("text-xs", colors?.textMuted)}>
            Drag items onto canvas to create stickies
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductElementsDrawer;
